import './styles.module.scss'

import { MaterialIcon } from '@ampcore/adel-react'
import {
  AmpcoreMaterialIconId,
  BackgroundColor
} from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSyncHeights } from '../../hooks/useSyncHeights'
import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { TestimonialProps } from './Testimonial.type'
import { TestimonialCard } from './TestimonialCard'

export const Testimonial = (props: TestimonialProps) => {
  const {
    backgroundColor,
    testimonialCardList = [],
    headline,
    introduction,
    variant = 'single-bubble',
    leftArrowDataAnalytics,
    rightArrowDataAnalytics
  } = props

  const atb = new AnalyticsTagBuilder().setDefaultName('Testimonial')

  const [position, setPosition] = useState(0)
  const [positionOffset, setPositionOffset] = useState(0)
  const [cardWidth, setCardWidth] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const testimonialComponentRef = useRef<HTMLDivElement>(null)
  const [startY, setStartY] = useState(0)
  const [startX, setStartX] = useState(0)
  const CARD_GAP = 20
  const CARD_PEEK = cardWidth === 325 && position > 0 ? 25 : 0

  const testimonialCardListData = useMemo(
    () =>
      Array.isArray(testimonialCardList)
        ? testimonialCardList
        : [testimonialCardList],
    [testimonialCardList]
  )

  const hasLeftCopy =
    testimonialCardListData.length > 1 || headline || introduction

  const testimonialCardRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([])
  if (testimonialCardRefs.current.length !== testimonialCardListData.length) {
    testimonialCardRefs.current = testimonialCardListData.map(
      (_, i) =>
        testimonialCardRefs.current[i] || React.createRef<HTMLDivElement>()
    )
  }

  const testimonialStatements = testimonialCardListData.map(
    item => item.testimonialStatement
  )

  useSyncHeights(testimonialCardRefs.current, [testimonialStatements])

  const moveLeft = useCallback(
    (jumpAmount: number) => {
      if (position === 0) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position - 1)
        return
      }
      if (position - jumpAmount <= 0) {
        setPosition(0)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position]
  )
  const moveRight = useCallback(
    (jumpAmount: number) => {
      if (
        !testimonialCardListData ||
        position === testimonialCardListData.length - 1
      ) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position + 1)
        return
      }
      if (position - jumpAmount >= testimonialCardListData.length) {
        setPosition(testimonialCardListData.length - 1)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position, testimonialCardListData]
  )
  const onSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaY) > 100) {
        return
      }
      const numCardsMoved = deltaX / (cardWidth + CARD_GAP)
      if (deltaX > 50) {
        moveLeft(Math.floor(numCardsMoved))
      }
      if (deltaX < -50) {
        moveRight(Math.ceil(numCardsMoved))
      }
    },
    [moveLeft, moveRight, cardWidth]
  )
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
      return
    }
    e.preventDefault()
    setIsSwiping(true)
    setStartX(e.touches[0].clientX)
    setStartY(e.touches[0].clientY)
  }, [])

  const handleTouch = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const deltaX = endX - startX
      setPositionOffset(deltaX)
    },
    [startX, setPositionOffset]
  )

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      const deltaX = endX - startX
      const deltaY = endY - startY
      onSwipe(deltaX, deltaY)
      setPositionOffset(0)
      setIsSwiping(false)
    },
    [startX, startY, onSwipe]
  )

  const updateHeight = () => {
    if (testimonialComponentRef.current) {
      if (window.matchMedia('screen and (max-width:1024px)').matches) {
        testimonialComponentRef.current.style.minHeight = 'auto'
      } else {
        testimonialComponentRef.current.style.minHeight = `${
          (wrapperRef.current?.offsetHeight || 0) + 130
        }px`
      }
    }
  }

  useEffect(() => {
    updateHeight()
  }, [wrapperRef.current?.offsetHeight])

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchmove', handleTouch)
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouch)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchEnd, handleTouch])

  const handleWindowSizeChange = useCallback(() => {
    if (variant === 'multi-bubble') {
      setCardWidth(325)
    } else if (window.matchMedia('screen and (max-width:1024px)').matches) {
      setCardWidth(325)
    } else {
      setCardWidth(750)
    }
  }, [variant])

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [handleWindowSizeChange])
  if (!testimonialCardListData) {
    return null
  }
  const gridColumnWidth = `${cardWidth}px`
  const gridStyle = {
    gridTemplateColumns: `repeat(${testimonialCardListData.length}, ${gridColumnWidth})`,
    right: position * (cardWidth + CARD_GAP) - positionOffset - CARD_PEEK
  }

  const renderControls = () => {
    return (
      <div className="Testimonial-cardCarouselControls u-flex u-flexAlignItemsEnd u-marginBottom4 u-posRelative">
        <button
          className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke"
          onClick={() => moveLeft(0)}
          disabled={position === 0}
          {...atb.dataAnalyticAttribute(leftArrowDataAnalytics, 'left-button')}
        >
          <MaterialIcon icon={AmpcoreMaterialIconId.CHEVRON_LEFT} />
          <span className="u-hiddenVisually">Slide Left</span>
        </button>
        <button
          className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke"
          onClick={() => moveRight(0)}
          disabled={position === testimonialCardListData.length - 1}
          {...atb.dataAnalyticAttribute(
            rightArrowDataAnalytics,
            'right-button'
          )}
        >
          <MaterialIcon icon={AmpcoreMaterialIconId.CHEVRON_RIGHT} />
          <span className="u-hiddenVisually">Slide Right</span>
        </button>
        <p className="u-textColorActionBlue u-textSizeMsrm1 Testimonial-cardCarousel--position">
          {position + 1} / {testimonialCardListData.length}
        </p>
        <div
          className="u-hiddenVisually"
          id="announce-slide"
          aria-live="polite"
        >
          {`You are currently on slide ${position + 1} of ${
            testimonialCardListData.length
          }`}
        </div>
      </div>
    )
  }

  return (
    <section
      className={`Testimonial u-flex u-sm-paddingVert48 ${
        backgroundColor === BackgroundColor.STONE && 'u-bgColorStone'
      }`}
      aria-labelledby="testimonial-heading"
    >
      <div className="Testimonial-container u-sizeConstrained u-flex u-sm-flexCol u-posRelative">
        {hasLeftCopy && (
          <div className="Testimonial-description--width u-sm-sizeFull u-flex u-flexAlignItemsCenter u-paddingRight40 u-sm-paddingHoriz20 u-sm-marginBottom32">
            <div className="u-sizeFull">
              {headline && (
                <h3
                  id="testimonial-heading"
                  className="u-textColorMidnightBlue u-textBold"
                  data-testid={props.testId ? `${props.testId}-headline` : null}
                >
                  {rteContentFilter(headline)}
                </h3>
              )}
              {introduction && (
                <div
                  className="u-marginTop20 Content content-block"
                  dangerouslySetInnerHTML={{
                    __html: rteContentFilter(introduction ?? '')
                  }}
                  data-testid={
                    props.testId ? `${props.testId}-introduction` : null
                  }
                ></div>
              )}
              {testimonialCardListData.length > 1 && (
                <div className="u-sm-hidden u-marginTop20">
                  {renderControls()}
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className={`Testimonial-overflowWrapper u-sm-sizeFull u-paddingHoriz20 ${
            variant === 'multi-bubble'
              ? 'Testimonial--multiBubble'
              : 'Testimonial--singleBubble'
          } ${!hasLeftCopy ? 'Testimonial--noLeftCopy' : ''}`}
        >
          <div
            className={`Testimonial-cardListWrapper u-posRelative ${
              isSwiping ? 'Testimonial-cardList--noTransition' : ''
            }`}
            style={gridStyle}
            ref={wrapperRef}
            data-testid={
              props.testId ? `${props.testId}-card-list-wrapper` : null
            }
          >
            {testimonialCardListData.map((card, index) => (
              <div
                key={`${card.personInfo}_${index}`}
                className="u-flex"
                aria-current={index === position}
                aria-hidden={index !== position}
              >
                <TestimonialCard
                  {...card}
                  variant={variant}
                  ref={testimonialCardRefs.current[index]}
                  testId={`testimonial-card-${index}`}
                />
              </div>
            ))}
          </div>
        </div>
        {testimonialCardListData.length > 1 && (
          <div className="u-lg-hidden u-md-hidden u-sm-marginTop24 u-sm-paddingTop24 u-sm-paddingHoriz20">
            {renderControls()}
          </div>
        )}
      </div>
    </section>
  )
}
